import { WORKFLOW_HOOK, WORKFLOW_HOOK_VARIABLE_PREFIX } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Field, Textarea } from "@sagot/ui";
import { useState } from "react";
import { IntegerInput } from "../../../workspace/shared";
import type { WaitForWebhookStep } from "../types";
import type { FlowContext } from "./flow-context";
import { HookInstructions } from "./hook-instructions";
import { exampleText, hookVariables, parseExampleBody, type ExampleProblem } from "./hook-model";
import { PathSelect } from "./path-select";
import { type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";
const EXAMPLE_ROWS = 6;

/** Wait for webhook: how long to wait, an example of what arrives (names the variables), and where the run goes next. */
export function HookWaitForm({ step, disabled, onChange, paths, canAddPath }: FormProps<WaitForWebhookStep> & Pick<FlowContext, "paths" | "canAddPath">) {
  const { t } = useTranslation();
  const [text, setText] = useState(() => exampleText(step.example_body));
  const [problem, setProblem] = useState<{ reason: ExampleProblem; key?: string } | null>(null);
  const variables = hookVariables(step.example_body);
  const edit = (next: string) => {
    setText(next);
    const parsed = parseExampleBody(next);
    if (!parsed.ok) {
      setProblem({ reason: parsed.problem, key: parsed.key });
      return;
    }
    setProblem(null);
    onChange({ ...step, example_body: parsed.body });
  };
  return (
    <div className="space-y-5">
      <Field label={t(`${F}.hookTimeout`)} hint={t(`${F}.hookTimeoutHint`, { max: WORKFLOW_HOOK.timeoutHoursMax })}>
        <IntegerInput min={WORKFLOW_HOOK.timeoutHoursMin} max={WORKFLOW_HOOK.timeoutHoursMax} disabled={disabled} value={step.timeout_hours} onChange={(timeout_hours) => { onChange({ ...step, timeout_hours }); }} />
      </Field>
      <Field
        label={t(`${F}.hookExample`)}
        hint={t(`${F}.hookExampleHint`, { keys: WORKFLOW_HOOK.exampleKeysMax, prefix: WORKFLOW_HOOK_VARIABLE_PREFIX })}
        error={problem ? t(`${F}.hookExampleProblems.${problem.reason}`, { key: problem.key ?? "", max: WORKFLOW_HOOK.exampleKeysMax, chars: WORKFLOW_HOOK.valueMax }) : undefined}
      >
        <Textarea className="font-mono text-xs" rows={EXAMPLE_ROWS} spellCheck={false} disabled={disabled} placeholder={t(`${F}.hookExamplePlaceholder`)} value={text} onChange={(e) => { edit(e.target.value); }} />
      </Field>
      {variables.length > 0 ? (
        <p className="text-xs text-fg-subtle" data-testid="hook-variables">
          {t(`${F}.hookVariables`)}:{" "}
          {variables.map((name, i) => (
            <span key={name}>
              {i > 0 ? ", " : ""}
              <code className="font-mono">{`{{${name}}}`}</code>
            </span>
          ))}
        </p>
      ) : null}
      <PathSelect label={t(`${F}.hookReceivedGoTo`)} hint={t(`${F}.hookReceivedHint`)} value={step.received_goto} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.nextStep`)} disabled={disabled} onChange={(received_goto) => { onChange({ ...step, received_goto }); }} />
      <PathSelect label={t(`${F}.hookTimeoutGoTo`)} hint={t(`${F}.hookTimeoutGoToHint`)} value={step.timeout_goto} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.endHere`)} disabled={disabled} onChange={(timeout_goto) => { onChange({ ...step, timeout_goto }); }} />
      <HookInstructions />
    </div>
  );
}
